import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { AlertTriangle, Undo2, RotateCcw, Save, Loader2, GripVertical, ArrowRightLeft, Plus, X } from "lucide-react";
import { AvailablePanel, AvailableFuturePanel } from "./AvailablePanel";
import { MoveDialog } from "./MoveDialog";
import { projectColor, fmtDate, initials } from "./timeline";

function buildSim(data) {
  return {
    projects: (data.sim_projects || data.by_project || []).map((r) => ({ project: r.project, members: r.members.map((m) => ({ ...m })) })),
    available: (data.available_all || data.available || []).map((e) => ({ ...e })),
  };
}

export function Simulator({ data, q, hideEmpty = false, onApplied }) {
  const [sim, setSim] = useState(() => buildSim(data));
  const [history, setHistory] = useState([]);
  const [moves, setMoves] = useState([]);
  const [dragging, setDragging] = useState(null); // { member, fromPid|null }
  const [overPid, setOverPid] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [boardIds, setBoardIds] = useState([]); // proiectele adăugate manual pe tablă
  const [pickerQ, setPickerQ] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => { setSim(buildSim(data)); setHistory([]); setMoves([]); }, [data]);

  const originalCounts = useMemo(
    () => Object.fromEntries((data.sim_projects || data.by_project || []).map((r) => [r.project.id, r.members.length])), [data]);
  const emptied = sim.projects.filter((p) => boardIds.includes(p.project.id) && p.members.length === 0 && originalCounts[p.project.id] > 0);

  const addProject = (pid) => { setBoardIds((ids) => (ids.includes(pid) ? ids : [...ids, pid])); setPickerQ(""); setPickerOpen(false); };
  const removeProject = (pid) => setBoardIds((ids) => ids.filter((x) => x !== pid));

  const pickerOptions = useMemo(() => {
    const s = pickerQ.trim().toLowerCase();
    return sim.projects
      .filter((p) => !boardIds.includes(p.project.id))
      .filter((p) => !s || p.project.name?.toLowerCase().includes(s) || p.project.code?.toLowerCase().includes(s))
      .slice(0, 40);
  }, [sim.projects, boardIds, pickerQ]);

  const visible = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    // afișăm DOAR proiectele adăugate pe tablă, în ordinea adăugării
    let r = boardIds.map((id) => sim.projects.find((p) => p.project.id === id)).filter(Boolean);
    if (s) r = r.filter((p) => p.project.name?.toLowerCase().includes(s) || p.project.code?.toLowerCase().includes(s));
    return r;
  }, [sim, q, boardIds]);

  function startDrag(ev, member, fromPid) {
    ev.dataTransfer.setData("text/plain", member.employee_id);
    ev.dataTransfer.effectAllowed = "move";
    setDragging({ member, fromPid });
  }
  function endDrag() { setDragging(null); setOverPid(null); }

  function dropOn(toPid) {
    setOverPid(null);
    if (!dragging) return;
    const { member, fromPid } = dragging;
    setDragging(null);
    if (fromPid === toPid) return;
    const target = sim.projects.find((p) => p.project.id === toPid);
    if (!target) return;
    if (target.members.some((m) => m.employee_id === member.employee_id)) {
      toast.error(`${member.name} este deja alocat pe ${target.project.code || target.project.name}`);
      return;
    }
    const source = fromPid ? sim.projects.find((p) => p.project.id === fromPid) : null;
    setPending({
      member, fromPid, toPid, from: source?.project || null, to: target.project,
      sourceWillBeEmpty: !!source && source.members.length === 1,
    });
  }

  function confirmMove({ start_date, end_date, pct }) {
    const { member, fromPid, toPid, from, to } = pending;
    setPending(null);
    setHistory((h) => [...h, { sim, moves }]);
    const fromLabel = from ? (from.code || from.name) : "disponibil";
    const idx = member._move_idx != null ? member._move_idx : moves.length;
    const srcPct = fromPid
      ? (member.allocated_hours || 0)
      : (member.free_pct != null ? member.free_pct : 100);
    const partial = pct < srcPct;
    const moved = {
      ...member, allocated_hours: pct, start_date, end_date,
      assignment_id: (fromPid && !partial) ? member.assignment_id : null,
      _moved_from: member._moved_from || fromLabel, _move_idx: idx,
    };
    setSim((s) => {
      let available = s.available;
      if (!fromPid) {
        // sursa e „Resurse disponibile": scade capacitatea liberă; scoate doar dacă ajunge la 0
        available = s.available
          .map((e) => e.id === member.employee_id
            ? { ...e, free_pct: Math.max(0, (e.free_pct != null ? e.free_pct : 100) - pct), alloc_pct: (e.alloc_pct || 0) + pct }
            : e)
          .filter((e) => e.id !== member.employee_id || (e.free_pct != null ? e.free_pct : 0) > 0);
      }
      const projects = s.projects.map((p) => {
        if (p.project.id === fromPid) {
          if (partial) {
            return { ...p, members: p.members.map((m) => m.employee_id === member.employee_id ? { ...m, allocated_hours: srcPct - pct } : m) };
          }
          return { ...p, members: p.members.filter((m) => m.employee_id !== member.employee_id) };
        }
        if (p.project.id === toPid) return { ...p, members: [...p.members, moved] };
        return p;
      });
      return { available, projects };
    });
    const mv = {
      assignment_id: fromPid ? (member.assignment_id || null) : null, employee_id: member.employee_id,
      to_project_id: toPid, start_date, end_date, pct,
      _label: `${member.name} (${pct}%): ${moved._moved_from} → ${to.code || to.name}`,
    };
    setMoves((list) => (member._move_idx != null ? list.map((x, i) => (i === idx ? mv : x)) : [...list, mv]));
    if (fromPid && !partial && from && (sim.projects.find((p) => p.project.id === fromPid)?.members.length === 1)) {
      toast.warning(`Proiectul ${from.name} rămâne fără resurse.`);
    } else {
      toast.success(`${member.name} · ${pct}% pe ${to.code || to.name} (nesalvat încă)`);
    }
  }

  function undo() {
    const last = history[history.length - 1];
    if (!last) return;
    setSim(last.sim); setMoves(last.moves); setHistory((h) => h.slice(0, -1));
  }
  function reset() { setSim(buildSim(data)); setMoves([]); setHistory([]); }

  async function apply() {
    if (!moves.length) return;
    if (emptied.length && !window.confirm(
      `Următoarele proiecte rămân fără resurse: ${emptied.map((p) => p.project.name).join(", ")}. Continui?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/pm/planner/apply", { moves: moves.map(({ _label, ...m }) => m) });
      const n = r.data.applied;
      toast.success(`${n} ${n === 1 ? "mutare aplicată" : "mutări aplicate"}${r.data.skipped ? `, ${r.data.skipped} ignorate` : ""}`);
      if (r.data.projects_without_resources?.length) {
        toast.warning(`Proiecte fără resurse: ${r.data.projects_without_resources.map((p) => p.code || p.name).join(", ")}`);
      }
      onApplied();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="simulator">
      <div className="surface-flat px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
        <ArrowRightLeft className="h-4 w-4 text-brand shrink-0" />
        <div className="text-[13px] text-slate-600 flex-1 min-w-[260px]">
          Trage o persoană de pe un proiect (sau din „Resurse disponibile") pe alt proiect. Nimic nu se salvează până nu apeși <b>Aplică modificările</b>.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="pio-badge pio-badge-blue" data-testid="sim-moves-count">{moves.length} {moves.length === 1 ? "mutare" : "mutări"}</span>
          <button className="pio-btn-ghost disabled:opacity-40" onClick={undo} disabled={!history.length} data-testid="sim-undo"><Undo2 /> Anulează ultima</button>
          <button className="pio-btn-ghost disabled:opacity-40" onClick={reset} disabled={!moves.length} data-testid="sim-reset"><RotateCcw /> Renunță</button>
          <button className="pio-btn disabled:opacity-50" onClick={apply} disabled={!moves.length || busy} data-testid="sim-apply">
            {busy ? <Loader2 className="animate-spin" /> : <Save />} Aplică modificările{moves.length ? ` (${moves.length})` : ""}
          </button>
        </div>
      </div>

      {emptied.length > 0 && (
        <div data-testid="sim-warning" className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div><b>Atenție:</b> {emptied.length === 1 ? "proiectul" : "proiectele"} <b>{emptied.map((p) => p.project.name).join(", ")}</b> {emptied.length === 1 ? "rămâne" : "rămân"} fără nicio resursă alocată.</div>
        </div>
      )}

      {moves.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="sim-moves-list">
          {moves.map((m, i) => <span key={i} className="pio-badge pio-badge-gray">{m._label}</span>)}
        </div>
      )}

      <div className="flex gap-4 items-start flex-col lg:flex-row">
        <div className="flex-1 min-w-0 w-full">
          {/* Selector proiecte — adaugă manual proiectele pe care vrei să lucrezi */}
          <div className="relative mb-3" data-testid="sim-project-picker">
            <div className="flex items-center gap-2 surface-flat px-3 py-2">
              <Plus className="h-4 w-4 text-brand shrink-0" />
              <input
                value={pickerQ}
                onFocus={() => setPickerOpen(true)}
                onChange={(e) => { setPickerQ(e.target.value); setPickerOpen(true); }}
                placeholder="Adaugă un proiect pe tablă (caută după cod sau nume)..."
                data-testid="sim-project-search"
                className="flex-1 bg-transparent outline-none text-[13px] text-slate-700 placeholder:text-slate-400" />
              {boardIds.length > 0 && (
                <button onClick={() => setBoardIds([])} data-testid="sim-board-clear"
                  className="text-[11px] text-slate-400 hover:text-slate-600">golește tabla</button>
              )}
            </div>
            {pickerOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setPickerOpen(false)} />
                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="sim-project-options">
                  {pickerOptions.length === 0 ? (
                    <div className="px-3 py-3 text-[12px] text-slate-400 text-center">Niciun proiect de adăugat.</div>
                  ) : pickerOptions.map((p) => (
                    <button key={p.project.id} onClick={() => addProject(p.project.id)}
                      data-testid={`sim-project-option-${p.project.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: projectColor(p.project.id) }} />
                      <span className="font-mono text-[10px] text-slate-400 shrink-0">{p.project.code}</span>
                      <span className="text-[13px] text-slate-700 truncate flex-1">{p.project.name}</span>
                      <span className="pio-badge pio-badge-gray shrink-0">{p.members.length}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {visible.length === 0 && (
              <div className="col-span-full py-12 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl" data-testid="sim-board-empty">
                Adaugă unul sau mai multe proiecte de mai sus pentru a începe să muți resurse.
              </div>
            )}
            {visible.map((row) => (
              <ProjectCard key={row.project.id} row={row} dragging={dragging} isOver={overPid === row.project.id}
                onDragOver={() => setOverPid(row.project.id)} onDragLeave={() => setOverPid(null)}
                onDrop={() => dropOn(row.project.id)} onDragStart={startDrag} onDragEnd={endDrag}
                onRemove={() => removeProject(row.project.id)} />
            ))}
          </div>
        </div>
        <div className="w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-4">
          <AvailablePanel items={sim.available} draggable
            onDragStart={(ev, e) => startDrag(ev, { employee_id: e.id, name: e.name, job_title: e.job_title, assignment_id: null, start_date: null, end_date: null, free_pct: e.free_pct != null ? e.free_pct : 100 }, null)}
            onDragEnd={endDrag} />
          <AvailableFuturePanel items={data.available_future || []} today={data.today} />
        </div>
      </div>

      <MoveDialog pending={pending} onConfirm={confirmMove} onCancel={() => setPending(null)} />
    </div>
  );
}

function ProjectCard({ row, dragging, isOver, onDragOver, onDragLeave, onDrop, onDragStart, onDragEnd, onRemove }) {
  const { project: p, members } = row;
  const color = projectColor(p.id);
  const canDrop = !!dragging && dragging.fromPid !== p.id;
  const cls = isOver ? "!border-brand bg-brand/[0.04] ring-2 ring-brand/30"
    : members.length === 0 ? "!border-red-300 bg-red-50/40"
    : canDrop ? "!border-dashed !border-slate-300" : "";
  return (
    <div data-testid={`sim-project-${p.id}`}
      onDragOver={(e) => { if (canDrop) { e.preventDefault(); onDragOver(); } }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      className={`surface-flat p-3 min-h-[120px] transition-colors ${cls}`}>
      <div className="flex items-start gap-2 mb-2">
        <span className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] text-slate-400 leading-tight">{p.code}{p.status === "completed" ? " · finalizat" : ""}</div>
          <div className="font-semibold text-[13px] text-slate-800 leading-tight truncate">{p.name}</div>
        </div>
        {members.length === 0 ? (
          <span className="pio-badge pio-badge-red" data-testid={`sim-empty-${p.id}`}><AlertTriangle className="h-3 w-3" /> Fără resurse</span>
        ) : (
          <span className="pio-badge pio-badge-gray">{members.length}</span>
        )}
        {onRemove && (
          <button onClick={onRemove} data-testid={`sim-project-remove-${p.id}`}
            title="Scoate de pe tablă"
            className="text-slate-300 hover:text-slate-600 shrink-0 -mr-1 -mt-0.5">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {members.length === 0 && (
          <div className="text-[11.5px] text-red-600/80 italic py-3 text-center">Trage aici o persoană pentru a o aloca</div>
        )}
        {members.map((m) => (
          <MemberChip key={m.employee_id} m={m} color={color}
            onDragStart={(ev) => onDragStart(ev, m, p.id)} onDragEnd={onDragEnd} />
        ))}
      </div>
    </div>
  );
}

function MemberChip({ m, color, onDragStart, onDragEnd }) {
  const period = m.permanent
    ? "permanent"
    : (m.start_date || m.end_date)
      ? `${fmtDate(m.start_date) || "…"} → ${fmtDate(m.end_date) || "fără termen"}`
      : "fără perioadă";
  return (
    <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} data-testid={`sim-member-${m.employee_id}`}
      className={`flex items-center gap-2 rounded-lg border bg-white px-2 py-1.5 cursor-grab active:cursor-grabbing hover:border-slate-300 transition-colors ${m._moved_from ? "border-brand/40 bg-brand/[0.03]" : "border-slate-200"}`}>
      <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
      <span className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white shrink-0" style={{ background: color }}>
        {initials(m.name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-slate-800 leading-tight truncate">{m.name}</div>
        <div className="text-[10.5px] text-slate-400 leading-tight truncate">
          {period}{m.allocated_hours != null ? ` · ${m.allocated_hours}%` : ""}
        </div>
      </div>
      {m._moved_from && <span className="pio-badge pio-badge-blue" title={`mutat de pe ${m._moved_from}`}>mutat</span>}
    </div>
  );
}
